import { type ConnectionTestDataItem, ConnectionTestResult } from '../types';

const RTT_LIMIT = 1;
const RTT_WARNING = 0.7;
const PACKETLOSS_LIMIT = 10;
const PACKETLOSS_WARNING = 5;

/**
 * Get test result from test data
 * @param {ConnectionTestDataItem[]} - Test data samples array
 * @returns {ConnectionTestResult} One of possible test results
 */
export function getResultFromTestData(
  testDataItems: ConnectionTestDataItem[],
): ConnectionTestResult {
  if (testDataItems.length === 0) {
    // connection failed so no stats available
    return ConnectionTestResult.Bad;
  }

  const rtt = getRttResultFromTestData(testDataItems);
  const quality = getQualityResultFromTestData(testDataItems);

  if (
    rtt === ConnectionTestResult.Good &&
    quality === ConnectionTestResult.Good
  ) {
    return ConnectionTestResult.Good;
  }

  if (
    rtt === ConnectionTestResult.Bad ||
    quality === ConnectionTestResult.Bad
  ) {
    return ConnectionTestResult.Bad;
  }

  return ConnectionTestResult.Warning;
}

/**
 * Get rtt result from test data, get max rtt and determne result based on it
 * @param {TestDataItem[]} - Test data samples array
 * @returns {ConnectionTestResult} One of possible test results
 */
export function getRttResultFromTestData(
  testDataItems: ConnectionTestDataItem[],
): ConnectionTestResult {
  const rttMax = Math.max(...testDataItems.map((item) => item.roundTripTime));

  if (rttMax === 0 || rttMax >= RTT_LIMIT) {
    return ConnectionTestResult.Bad;
  }
  if (rttMax >= RTT_WARNING) {
    return ConnectionTestResult.Warning;
  }
  return ConnectionTestResult.Good;
}

/**
 * Get quality result from test data which based on packet loss
 * Takes video packet loss if available, audio if only audio is available
 * @param {ConnectionTestDataItem[]} - Test data samples array
 * @returns {ConnectionTestResult} One of possible test results
 */
export function getQualityResultFromTestData(
  testDataItems: ConnectionTestDataItem[],
): ConnectionTestResult {
  const testDataItem = testDataItems.pop();

  if (
    testDataItem?.videoPacketLoss === undefined &&
    testDataItem?.audioPacketLoss === undefined
  ) {
    return ConnectionTestResult.Bad;
  }

  // take video if available as it will be more representative.
  return getQualityResultFromPacketLoss(
    testDataItem.videoPacketLoss ?? testDataItem.audioPacketLoss,
  );
}

/**
 * Get result from specific packet loss value
 * @param {number} - packet loss from WebRTC stats
 * @returns {ConnectionTestResult} One of possible test results
 */
export function getQualityResultFromPacketLoss(
  packetLoss?: number,
): ConnectionTestResult {
  if (packetLoss === undefined || packetLoss >= PACKETLOSS_LIMIT) {
    return ConnectionTestResult.Bad;
  }
  if (packetLoss >= PACKETLOSS_WARNING) {
    return ConnectionTestResult.Warning;
  }
  return ConnectionTestResult.Good;
}
